import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createLeadIdempotencyKey,
  getLeadSubmitGuard,
} from './useLeadSubmitGuard';
import { LEAD_USER_MESSAGES, type LeadSubmitResult, type LeadSubmitStatus } from '../services/leadTypes';
import type { LandingPageProps } from '../components/landing-templates/landingPageProps';

export interface UseT05LeadFormOptions {
  onLeadSubmit: LandingPageProps['onLeadSubmit'];
  defaultInterest?: string;
  quelleHero?: string;
  quelleModal?: string;
  /** Liest ?openLead=1 und öffnet das Modal (Startseite + Produktseiten). */
  watchOpenLeadParam?: boolean;
}

export function useT05LeadForm({
  onLeadSubmit,
  defaultInterest = 'Analyse anfragen',
  quelleHero = 't05-hero-inline',
  quelleModal = 't05-modal',
  watchOpenLeadParam = false,
}: UseT05LeadFormOptions) {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [heroEmail, setHeroEmail] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: defaultInterest,
    hpTrap: '',
    quelle: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState<'form' | 'success'>('form');
  const [leadSuccessStatus, setLeadSuccessStatus] = useState<Extract<LeadSubmitStatus, 'success' | 'partial'>>('success');
  const [leadFeedback, setLeadFeedback] = useState<string | null>(null);
  const [leadFeedbackKind, setLeadFeedbackKind] = useState<'success' | 'warning' | 'error'>('error');
  const heroFeedbackRef = useRef<HTMLParagraphElement>(null);

  const showLeadFeedback = useCallback((message: string, kind: 'success' | 'warning' | 'error' = 'error') => {
    setLeadFeedback(message);
    setLeadFeedbackKind(kind);
  }, []);

  const clearLeadFeedback = useCallback(() => {
    setLeadFeedback(null);
    setLeadFeedbackKind('error');
  }, []);

  useEffect(() => {
    if (leadFeedback && !isLeadModalOpen && heroFeedbackRef.current) {
      heroFeedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [leadFeedback, isLeadModalOpen]);

  useEffect(() => {
    if (!watchOpenLeadParam) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('openLead') === '1') {
      setFormData((prev) => ({
        ...prev,
        interest: decodeURIComponent(params.get('interest') || defaultInterest),
        hpTrap: '',
      }));
      setFormStep('form');
      setLeadSuccessStatus('success');
      clearLeadFeedback();
      setIsLeadModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
    }

    const src = params.get('utm_source') || '';
    const med = params.get('utm_medium') || '';
    const camp = params.get('utm_campaign') || '';
    if (src || med || camp) {
      const quelle = [src, med, camp].filter(Boolean).join(' / ');
      setFormData((prev) => ({ ...prev, quelle }));
    }
  }, [watchOpenLeadParam, defaultInterest, clearLeadFeedback]);

  const openDemo = useCallback(
    (interestOverride?: string) => {
      setFormStep('form');
      setLeadSuccessStatus('success');
      clearLeadFeedback();
      setFormData((prev) => ({
        ...prev,
        interest: interestOverride?.trim() || defaultInterest,
        hpTrap: '',
      }));
      setIsLeadModalOpen(true);
    },
    [clearLeadFeedback, defaultInterest],
  );

  const closeLeadModal = useCallback(() => {
    setIsLeadModalOpen(false);
  }, []);

  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLeadModalOpen && formData.hpTrap.trim()) {
      showLeadFeedback('Die Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.');
      return;
    }

    const email = (isLeadModalOpen ? formData.email : heroEmail).trim();
    if (!email) {
      showLeadFeedback('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      return;
    }

    const guard = getLeadSubmitGuard(email);
    if (!guard.canSubmit) {
      showLeadFeedback(guard.message || 'Anfrage bereits gesendet.');
      return;
    }

    setIsSubmitting(true);
    clearLeadFeedback();

    const payload = isLeadModalOpen
      ? {
          name: formData.name,
          email,
          company: formData.company,
          interest: formData.interest || defaultInterest,
          quelle: formData.quelle || quelleModal,
          website: '',
          idempotencyKey: createLeadIdempotencyKey(email),
        }
      : {
          name: '',
          email,
          company: '',
          interest: defaultInterest,
          quelle: quelleHero,
          website: '',
          idempotencyKey: createLeadIdempotencyKey(email),
        };

    try {
      const result: LeadSubmitResult = await onLeadSubmit(payload);

      if (result.blocked) {
        showLeadFeedback(result.message || 'Anfrage bereits gesendet.');
        return;
      }

      if (!result.ok || result.status === 'error') {
        showLeadFeedback(result.message || LEAD_USER_MESSAGES.networkError);
        return;
      }

      const feedbackKind = result.status === 'partial' ? 'warning' : 'success';
      const feedbackMessage = result.message || LEAD_USER_MESSAGES.success;

      if (isLeadModalOpen) {
        setLeadSuccessStatus(result.status === 'partial' ? 'partial' : 'success');
        setFormStep('success');
        return;
      }

      setHeroEmail('');
      showLeadFeedback(feedbackMessage, feedbackKind);
    } catch (error) {
      console.error('[Lead] submit error', error);
      showLeadFeedback(LEAD_USER_MESSAGES.networkError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    heroEmail,
    setHeroEmail,
    formData,
    setFormData,
    isLeadModalOpen,
    isSubmitting,
    formStep,
    leadSuccessStatus,
    leadFeedback,
    leadFeedbackKind,
    heroFeedbackRef,
    openDemo,
    closeLeadModal,
    submitLead,
  };
}

export type T05LeadFormState = ReturnType<typeof useT05LeadForm>;
